import React from 'react';
import { TagTypes } from '../../../Api/Types/tagTypes';
import { bankDocumentsApi } from '../Api/bankDocumentsApi';
import { normalizePositiveInteger } from './lookupPagination';
import {
  mapInvoiceToBankDocumentLookup,
  type BankDocumentInvoiceLookup,
  type BankDocumentInvoiceLookupSource,
} from './invoicesLookupMapper';

interface BankDocumentInvoiceLookupArgs {
  searchTerm?: string;
  pageNumber?: number;
  pageSize?: number;
  companyId?: number | null;
}

interface BankDocumentLookupRequest {
  searchTerm?: string;
  pageNumber?: number;
  pageSize?: number;
  take?: number;
  companyId?: number | null;
}

interface BankDocumentInvoicesPagedResponse {
  items?: BankDocumentInvoiceLookupSource[] | null;
  totalCount?: number;
  pageNumber?: number;
  pageSize?: number;
}

export const buildBankDocumentInvoicesLookupUrl = (
  request: BankDocumentLookupRequest
): string => {
  const queryParams = new URLSearchParams();
  const normalizedSearchTerm = request.searchTerm?.trim();
  const normalizedPageNumber = normalizePositiveInteger(request.pageNumber, 1);
  const normalizedPageSize = normalizePositiveInteger(request.pageSize, 10);

  if (normalizedSearchTerm) {
    queryParams.set('search', normalizedSearchTerm);
  }

  if (request.companyId !== null && request.companyId !== undefined) {
    queryParams.set('companyId', request.companyId.toString());
  }

  queryParams.set(
    'pageNumber',
    normalizedPageNumber.toString()
  );
  queryParams.set('pageSize', normalizedPageSize.toString());
  queryParams.set('take', normalizedPageSize.toString());

  return `/bank-documents/lookup/invoices?${queryParams.toString()}`;
};

const bankDocumentInvoicesLookupApi = bankDocumentsApi.injectEndpoints({
  endpoints: (builder) => ({
    getBankDocumentInvoicesLookupRaw: builder.query<
      BankDocumentInvoicesPagedResponse,
      BankDocumentLookupRequest
    >({
      query: buildBankDocumentInvoicesLookupUrl,
      providesTags: [{ type: TagTypes.BankDocument, id: TagTypes.List }],
    }),
  }),
});

const { useGetBankDocumentInvoicesLookupRawQuery } = bankDocumentInvoicesLookupApi;

type InvoicesLookupQueryOptions = Parameters<typeof useGetBankDocumentInvoicesLookupRawQuery>[1];
type InvoicesLookupQueryResult = ReturnType<typeof useGetBankDocumentInvoicesLookupRawQuery>;
type BankDocumentInvoicesLookupQueryResult = Pick<
  InvoicesLookupQueryResult,
  'isLoading' | 'isFetching' | 'isError' | 'error' | 'refetch'
> & {
  data: BankDocumentInvoiceLookup[];
  pagination: {
    totalCount: number;
    pageNumber: number;
    pageSize: number;
  };
};

export const useGetBankDocumentInvoicesLookupQuery = (
  args: BankDocumentInvoiceLookupArgs,
  options?: InvoicesLookupQueryOptions
): BankDocumentInvoicesLookupQueryResult => {
  const queryOptions: InvoicesLookupQueryOptions = options;
  const normalizedSearchTerm = args.searchTerm?.trim() || undefined;
  const normalizedPageNumber = normalizePositiveInteger(args.pageNumber, 1);
  const normalizedPageSize = normalizePositiveInteger(args.pageSize, 10);

  const queryResult = useGetBankDocumentInvoicesLookupRawQuery(
    {
      searchTerm: normalizedSearchTerm,
      pageNumber: normalizedPageNumber,
      pageSize: normalizedPageSize,
      take: normalizedPageSize,
      companyId: args.companyId ?? null,
    },
    queryOptions
  );

  const pagedResponse = queryResult.data;

  const data = React.useMemo(
    () =>
      (pagedResponse?.items ?? [])
        .map((invoice: BankDocumentInvoiceLookupSource) => mapInvoiceToBankDocumentLookup(invoice))
        .filter((item: BankDocumentInvoiceLookup | undefined): item is BankDocumentInvoiceLookup =>
          Boolean(item)
        ),
    [pagedResponse]
  );

  return {
    data,
    isLoading: queryResult.isLoading,
    isFetching: queryResult.isFetching,
    isError: queryResult.isError,
    error: queryResult.error,
    refetch: queryResult.refetch,
    pagination: {
      totalCount: pagedResponse?.totalCount ?? 0,
      pageNumber: pagedResponse?.pageNumber ?? normalizedPageNumber,
      pageSize: pagedResponse?.pageSize ?? normalizedPageSize,
    },
  };
};
